





































































import type { ImageNode, EBlockWidth } from '@getaccept/editor-lib';
import { getUnsplashAppId, RowType, SlideShowHelper } from '@getaccept/editor-lib';
import type { PropType, Ref } from '@vue/composition-api';
import type Vue from 'vue';
import { nextTick, defineComponent, computed, ref, watch } from '@vue/composition-api';
import ProsemirrorViewerInput from './ProsemirrorViewerInput.vue';
import ImageWithTransforms from './ImageWithTransforms.vue';
import EditorNodeWrapper from './EditorNodeWrapper.vue';

export default defineComponent({
  name: 'EditorNodeImage',
  components: {
    ImageWithTransforms,
    ProsemirrorViewerInput,
    EditorNodeWrapper,
  },
  props: {
    node: { type: Object as PropType<ImageNode>, default: () => ({}) },
    rowType: { type: String as PropType<RowType>, default: () => RowType.Standard },
    parentFirstChild: { type: Boolean },
    parentLastChild: { type: Boolean },
    blockWidthClass: {
      type: String as PropType<EBlockWidth>,
      default: null,
    },
    pageWidth: { type: Number, default: 0 },
    isBlockToggled: { type: Boolean },
    isGridView: { type: Boolean },
  },
  setup(props) {
    const imageContent: Ref<HTMLElement> = ref(null);
    const headingInput: Ref<Vue> = ref(null);
    const isTextLoaded = ref(false);
    const minTextSize = 12;
    const maxTextSize = 64;
    const textFont = ref({ size: maxTextSize });

    const isHeadingSet = computed(() => props.node.heading !== '<p></p>' && !!props.node.heading);
    const isCaptionSet = computed(() => props.node.caption !== '<p></p>' && !!props.node.caption);
    const fontSize = computed(() => `--font-size: ${textFont.value?.size}px;`);
    const imageSize = computed(() => props.node.imageSize?.toLowerCase());
    const imageAlign = computed(() => props.node.imageAlign?.toLowerCase());
    const rowTypeClass = computed(() => `row-type-${props.rowType?.toLowerCase()}`);
    const unsplashBeaconSrcUrl = computed(
      () =>
        `https://views.unsplash.com/v?app_id=${getUnsplashAppId()}&photo_id=${
          props.node.externalId
        }`
    );
    const slideNumber = computed(() => {
      const displayName = props.node?.displayName;
      return SlideShowHelper.extractSlideNrFromName(displayName);
    });

    const setTextSize = () => {
      if (!isHeadingSet.value) {
        return;
      }

      const textNode = imageContent.value?.querySelector('.image-heading p');

      if (!textNode) {
        return;
      }

      isTextLoaded.value = true;
      const parentContainerWidth = headingInput.value.$el.clientWidth;
      const currentTextWidth = textNode.scrollWidth;
      const currentFontSize = maxTextSize;

      const newValue = Math.floor(
        Math.min(
          Math.max(minTextSize, (parentContainerWidth / currentTextWidth) * currentFontSize),
          maxTextSize
        )
      );

      textFont.value = {
        ...textFont.value,
        size: newValue,
      };
    };

    const resetTextSize = async () => {
      textFont.value.size = maxTextSize;
      await nextTick();
      setTextSize();
    };

    const handleTextLoaded = () => {
      if (isTextLoaded.value) {
        return;
      }
      resetTextSize();
    };

    watch(
      () => props.isBlockToggled,
      () => {
        if (!props.isBlockToggled) {
          return;
        }

        resetTextSize();
      }
    );

    watch(
      () => props.pageWidth,
      () => {
        resetTextSize();
      }
    );

    return {
      RowType,
      imageContent,
      headingInput,
      isHeadingSet,
      isCaptionSet,
      fontSize,
      imageSize,
      imageAlign,
      rowTypeClass,
      unsplashBeaconSrcUrl,
      handleTextLoaded,
      resetTextSize,
      setTextSize,
      textFont,
      isTextLoaded,
      slideNumber,
    };
  },
});
